import React from 'react';
import { CpuIcon, HardDriveIcon, ThermometerIcon, GpuIcon, ActivityIcon, MessageSquareIcon, ClockIcon, ShieldIcon, AlertTriangleIcon, ArrowUpIcon, ArrowDownIcon, ArrowRightIcon, CheckCircleIcon, ServerIcon, AlertCircleIcon, MessageSquareTextIcon } from 'lucide-react';
const Dashboard = () => {
  return <div className="space-y-4" data-id="element-81">
      {/* Header Section */}
      <div className="flex justify-between items-center" data-id="element-82">
        <h1 className="text-xl font-bold text-gray-800" data-id="element-83">
          System Health Monitoring
        </h1>
        <div className="flex space-x-2" data-id="element-84">
          <div className="flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-md text-sm" data-id="element-85">
            <CheckCircleIcon size={14} className="mr-1 text-green-500" data-id="element-86" />
            <span className="font-medium" data-id="element-87">System Healthy</span>
          </div>
          <div className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm" data-id="element-88">
            <ServerIcon size={14} className="mr-1" data-id="element-89" />
            <span className="font-medium" data-id="element-90">Llama-3-70B Loaded</span>
          </div>
        </div>
      </div>
      {/* Resource Utilization Section */}
      <section data-id="element-91">
        <h2 className="text-base font-semibold text-gray-700 mb-2" data-id="element-92">
          Resource Utilization
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-id="element-93">
          <ResourceCard title="GPU Usage" value="85%" icon={<GpuIcon size={16} data-id="element-95" />} color="bg-red-500" data-id="element-94" />
          <ResourceCard title="CPU Usage" value="45%" icon={<CpuIcon size={16} data-id="element-97" />} color="bg-yellow-500" data-id="element-96" />
          <ResourceCard title="Memory Usage" value="38/40 GB" icon={<ServerIcon size={16} data-id="element-99" />} color="bg-blue-500" percentage={95} data-id="element-98" />
          <ResourceCard title="Disk Usage" value="750/1000 GB" icon={<HardDriveIcon size={16} data-id="element-101" />} color="bg-green-500" percentage={75} data-id="element-100" />
        </div>
      </section>
      {/* Activity Metrics Section */}
      <section data-id="element-102">
        <h2 className="text-base font-semibold text-gray-700 mb-2" data-id="element-103">
          Activity Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-104">
          <MetricCard title="API Call Rate (1hr)" value="1,245" icon={<ActivityIcon size={16} className="text-blue-500" data-id="element-106" />} changeText="12% from last hour" changeType="increase" data-id="element-105" />
          <MetricCard title="Messages Sent" value="8,932" icon={<MessageSquareIcon size={16} className="text-blue-500" data-id="element-108" />} changeText="5% from yesterday" changeType="increase" data-id="element-107" />
          <MetricCard title="Failed API Requests (1hr)" value="23" icon={<AlertCircleIcon size={16} className="text-red-500" data-id="element-110" />} changeText="7% from last hour" changeType="decrease" decreaseIsGood={true} data-id="element-109" />
          <MetricCard title="Tokens Processed (1hr)" value="2.4M" icon={<MessageSquareTextIcon size={16} className="text-blue-500" data-id="element-112" />} changeText="15% from last hour" changeType="increase" data-id="element-111" />
          <MetricCard title="Tokens Processed (10 mins)" value="450K" icon={<MessageSquareTextIcon size={16} className="text-blue-500" data-id="element-114" />} changeText="2% change" changeType="neutral" data-id="element-113" />
          <MetricCard title="Average Response (5 min)" value="1.2s" subtext="latency" icon={<ClockIcon size={16} className="text-blue-500" data-id="element-116" />} changeText="10% improvement" changeType="decrease" decreaseIsGood={true} data-id="element-115" />
        </div>
      </section>
      {/* Guardrails & Critical Events Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-117">
        {/* Guardrails Section */}
        <section data-id="element-118">
          <div className="flex items-center justify-between mb-2" data-id="element-119">
            <h2 className="text-base font-semibold text-gray-700" data-id="element-120">
              Guardrails
            </h2>
            <div className="bg-yellow-100 text-yellow-800 px-2 py-0.5 text-xs rounded-full" data-id="element-121">
              24 blocks in last hour
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm" data-id="element-122">
            <div className="space-y-2" data-id="element-123">
              <GuardrailItem type="harmful" label="Harmful content" count={12} data-id="element-124" />
              <GuardrailItem type="pii" label="PII data" count={8} data-id="element-125" />
              <GuardrailItem type="code" label="Code execution" count={4} data-id="element-126" />
            </div>
          </div>
        </section>
        {/* Critical Events Section */}
        <section data-id="element-127">
          <h2 className="text-base font-semibold text-gray-700 mb-2" data-id="element-128">
            Recent Critical Events
          </h2>
          <div className="bg-white p-3 rounded-lg shadow-sm" data-id="element-129">
            <div className="space-y-2" data-id="element-130">
              <CriticalEvent type="error" message="GPU 2 temperature exceeding threshold" time="2 min ago" data-id="element-131" />
              <CriticalEvent type="warning" message="API rate limit reached for key ABC123" time="15 min ago" data-id="element-132" />
              <CriticalEvent type="info" message="Model swap completed: Llama-3-70B" time="45 min ago" data-id="element-133" />
            </div>
          </div>
        </section>
      </div>
    </div>;
};
const ResourceCard = ({
  title,
  value,
  icon,
  color,
  percentage
}) => {
  // If percentage is not provided, extract it from the value (e.g., "85%")
  const progressPercentage = percentage || parseInt(value);
  return <div className="bg-white p-3 rounded-lg shadow-sm" data-id="element-134">
      <div className="flex items-center justify-between mb-1" data-id="element-135">
        <h3 className="text-xs font-medium text-gray-500" data-id="element-136">{title}</h3>
        <span className="text-gray-400" data-id="element-137">{icon}</span>
      </div>
      <div className="text-lg font-semibold mb-1" data-id="element-138">{value}</div>
      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-139">
        <div className={`${color} h-1.5 rounded-full`} style={{
        width: `${progressPercentage}%`
      }} data-id="element-140"></div>
      </div>
    </div>;
};
const MetricCard = ({
  title,
  value,
  subtext,
  icon,
  changeText,
  changeType,
  decreaseIsGood
}) => {
  let changeIcon;
  let changeColor;
  if (changeType === 'increase') {
    changeIcon = <ArrowUpIcon size={12} data-id="element-141" />;
    changeColor = decreaseIsGood ? 'text-red-500' : 'text-green-500';
  } else if (changeType === 'decrease') {
    changeIcon = <ArrowDownIcon size={12} data-id="element-142" />;
    changeColor = decreaseIsGood ? 'text-green-500' : 'text-red-500';
  } else {
    changeIcon = <ArrowRightIcon size={12} data-id="element-143" />;
    changeColor = 'text-gray-500';
  }
  return <div className="bg-white p-3 rounded-lg shadow-sm" data-id="element-144">
      <div className="flex items-center justify-between mb-1" data-id="element-145">
        <h3 className="text-xs font-medium text-gray-500" data-id="element-146">{title}</h3>
        <span data-id="element-147">{icon}</span>
      </div>
      <div className="flex items-baseline" data-id="element-148">
        <div className="text-xl font-semibold" data-id="element-149">{value}</div>
        {subtext && <div className="ml-1 text-gray-500 text-xs" data-id="element-150">{subtext}</div>}
      </div>
      <div className={`flex items-center mt-1 ${changeColor}`} data-id="element-151">
        {changeIcon}
        <span className="ml-1 text-xs" data-id="element-152">{changeText}</span>
      </div>
    </div>;
};
const GuardrailItem = ({
  type,
  label,
  count
}) => {
  let icon;
  let color;
  switch (type) {
    case 'harmful':
      icon = <AlertCircleIcon size={14} data-id="element-153" />;
      color = 'text-red-500';
      break;
    case 'pii':
      icon = <AlertTriangleIcon size={14} data-id="element-154" />;
      color = 'text-yellow-500';
      break;
    case 'code':
      icon = <ShieldIcon size={14} data-id="element-155" />;
      color = 'text-blue-500';
      break;
    default:
      icon = <ShieldIcon size={14} data-id="element-156" />;
      color = 'text-gray-500';
  }
  return <div className="flex items-center justify-between" data-id="element-157">
      <div className="flex items-center" data-id="element-158">
        <span className={`mr-1 ${color}`} data-id="element-159">{icon}</span>
        <span className="text-sm font-medium" data-id="element-160">{label}</span>
      </div>
      <div className="text-sm font-medium" data-id="element-161">{count} blocks</div>
    </div>;
};
const CriticalEvent = ({
  type,
  message,
  time
}) => {
  let icon;
  let color;
  switch (type) {
    case 'error':
      icon = <AlertCircleIcon size={14} data-id="element-162" />;
      color = 'text-red-500';
      break;
    case 'warning':
      icon = <AlertTriangleIcon size={14} data-id="element-163" />;
      color = 'text-yellow-500';
      break;
    case 'info':
      icon = <ServerIcon size={14} data-id="element-164" />;
      color = 'text-blue-500';
      break;
    default:
      icon = <AlertCircleIcon size={14} data-id="element-165" />;
      color = 'text-gray-500';
  }
  return <div className="flex items-start" data-id="element-166">
      <span className={`mr-1 mt-0.5 ${color}`} data-id="element-167">{icon}</span>
      <div data-id="element-168">
        <div className="text-sm font-medium" data-id="element-169">{message}</div>
        <div className="text-xs text-gray-500" data-id="element-170">{time}</div>
      </div>
    </div>;
};
export default Dashboard;